import { useEffect, useState } from 'react';
import { WEB_SECTIONS, strapCaps, type WebArticle } from '@airpost/shared';
import { ApiError, post } from '../api.js';
import { t } from '../i18n.js';
import { Link } from '../router.js';

/**
 * The story as an article on the station's news portal, alphanews.live (ADR 090): the portal's own page (its logo,
 * menu, advertisement slots, the "Takeaways" box, the floating live-feed button) round the article the API drafted,
 * with AirPost's own strip over it saying where the article came from (the AI assistant or the plain draft) and
 * offering to write it again. The page is the article ready to be published: the portal's CMS is not connected,
 * so Δημοσίευση is a STUB that says so. The portal is Greek, whatever the user's locale.
 */
export interface WebArticleDto {
  ok: true;
  storyId: string;
  revision: number;
  article: WebArticle;
  source: 'ai' | 'plain';
  model: string | null;
  aiError: string | null;
  aiEnabled: boolean;
  image: string | null;
  categoryName: string | null;
  path: string;
  draftedAt: string;
}

/** The portal's menu after the search: its sections, then its two magazines. */
const MENU = [...WEB_SECTIONS, 'Newsauto CY', 'Hello Cyprus'];
/** The portal sets its texts in a geometric sans; Inter stands in for it where the portal's own fonts are not licensed. */
const FONT_ID = 'wa-font';
const FONT_HREF = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;700;800&display=swap';

/** The day, date and time as the portal's masthead prints them, in Cyprus time. */
export function portalClock(d: Date): { day: string; date: string; time: string } {
  const tz = { timeZone: 'Europe/Nicosia' } as const;
  const day = strapCaps(new Intl.DateTimeFormat('el-GR', { ...tz, weekday: 'long' }).format(d));
  const date = new Intl.DateTimeFormat('en-GB', { ...tz, day: '2-digit', month: '2-digit', year: 'numeric' }).format(d).replace(/\//g, '.');
  const time = new Intl.DateTimeFormat('en-GB', { ...tz, hour: '2-digit', minute: '2-digit', hour12: false }).format(d);
  return { day, date, time };
}

function Advertisement({ tall }: { tall?: boolean }) {
  return (
    <div className={`wa-ad${tall ? ' tall' : ''}`} aria-hidden="true">
      <span>Advertisement</span>
      <div className="wa-ad-slot" />
    </div>
  );
}

export function WebArticlePage({ id }: { id: string }) {
  const [data, setData] = useState<WebArticleDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [opened] = useState(() => new Date());

  const load = (fresh: boolean) => {
    setBusy(true);
    setError(null);
    setNote(null);
    post<WebArticleDto>(`/api/stories/${id}/web-article`, { fresh })
      .then(setData)
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)))
      .finally(() => setBusy(false));
  };
  useEffect(() => {
    if (!document.getElementById(FONT_ID)) {
      const link = document.createElement('link');
      link.id = FONT_ID;
      link.rel = 'stylesheet';
      link.href = FONT_HREF;
      document.head.appendChild(link);
    }
    // after the render (not in it), and once: a mount that React repeats in development asks the model once
    const timer = setTimeout(() => load(false), 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  useEffect(() => {
    if (!data) return;
    const was = document.title;
    document.title = `${data.article.title} - alphanews.live`;
    return () => {
      document.title = was;
    };
  }, [data]);

  const a = data?.article ?? null;
  const when = portalClock(data ? new Date(data.draftedAt) : opened);
  const sourceText = !data ? '' : data.source === 'ai' ? `✦ ${t('waByAi')}${data.model ? ` · ${data.model}` : ''}` : data.aiError ? t('waAiFailed') : t('waPlain');
  return (
    <div className="wa">
      <div className="wa-bar">
        <div className="wa-bar-left">
          <Link to={`/story/${id}`} className="wa-back">
            ← {t('waBack')}
          </Link>
          <b>{t('waPreview')}</b>
          <span className="wa-tag">{t('waDraft')}</span>
          {data && (
            <span className={`wa-src${data.source === 'ai' ? ' ai' : ''}`} title={data.aiError ?? data.model ?? ''}>
              {sourceText}
            </span>
          )}
          {data && <span className="wa-url">alphanews.live{data.path}</span>}
        </div>
        <div className="wa-bar-right">
          <button type="button" className="btn" disabled={busy || !data?.aiEnabled} title={data && !data.aiEnabled ? t('aiDisabled') : ''} onClick={() => load(true)}>
            {busy ? '…' : `↻ ${t('waAgain')}`}
          </button>
          <button type="button" className="btn primary" disabled={busy || !data} onClick={() => setNote(t('waPublishStub'))}>
            ↗ {t('waPublish')}
          </button>
        </div>
      </div>
      {note && (
        <div className="wa-note">
          <b>STUB</b> {note}
          <button type="button" className="btn small" onClick={() => setNote(null)}>
            ✕
          </button>
        </div>
      )}
      {error && <div className="wa-note err">{error}</div>}

      <div className="wa-site">
        <header className="wa-top">
          <div className="wa-date">
            <span>{when.day}</span>
            <span>
              {when.date} <i>{when.time}</i>
            </span>
          </div>
          <div className="wa-brand">
            <div className="wa-lang">
              <b>Ελληνικά</b>
              <span>|</span>English
            </div>
            <img className="wa-logo" src="/brand/alphanews-logo.png" alt="alphanews.live" width={522} height={76} />
          </div>
          <div className="wa-group">
            <span>
              Μέλος του ομίλου
              <br />
              Alpha Cyprus
            </span>
            <img src="/brand/alpha-letter.svg" alt="Alpha" width={56} height={60} />
          </div>
        </header>
        <nav className="wa-nav" aria-hidden="true">
          <span className="wa-pill search">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
              <circle cx="10.5" cy="10.5" r="6.5" />
              <path d="M15.5 15.5 21 21" />
            </svg>
            Αναζήτηση
          </span>
          {MENU.map((m) => (
            <span key={m} className={`wa-pill${a && m === a.section ? ' on' : ''}`}>
              {m}
            </span>
          ))}
          <span className="wa-pill red">ALPHA της Κυριακής</span>
          <span className="wa-pill black">Podcasts</span>
        </nav>
        <Advertisement />

        <article className="wa-article">
          {!a ? (
            <div className="wa-writing">{error ? error : <><span className="wa-spin" />{t('waWriting')}</>}</div>
          ) : (
            <>
              {data!.image ? (
                <img className="wa-hero" src={data!.image} alt="" />
              ) : (
                <div className="wa-hero none">
                  <img src="/brand/alpha-letter.svg" alt="" width={84} height={90} />
                </div>
              )}
              <div className="wa-meta">
                <span>{when.date}</span>
                <span>
                  <b>{strapCaps(data!.categoryName ?? a.section)}</b> {data!.categoryName && <i>{strapCaps(a.section)}</i>}
                </span>
                <span>{when.time}</span>
              </div>
              <h1 className="wa-h1">{a.title}</h1>
              <div className="wa-cols">
                {a.subtitle && <h2 className="wa-h2">{a.subtitle}</h2>}
                <div className="wa-by">{a.byline}</div>
                <div className="wa-main">
                  {a.takeaways.length > 0 && (
                    <section className="wa-take">
                      <div className="wa-take-head">
                        <b>
                          Takeaways by <span>alphanews.live</span>
                        </b>
                        <span className="wa-take-about">About This Summary ⓘ</span>
                      </div>
                      <ul>
                        {a.takeaways.map((x, i) => (
                          <li key={i}>{x}</li>
                        ))}
                      </ul>
                    </section>
                  )}
                  <div className="wa-body">
                    {a.paragraphs.map((p, i) => (
                      <p key={i}>{p}</p>
                    ))}
                  </div>
                  {a.tags.length > 0 && (
                    <div className="wa-tags">
                      {a.tags.map((x) => (
                        <span key={x}>#{x}</span>
                      ))}
                    </div>
                  )}
                </div>
                <aside className="wa-side">
                  <Advertisement tall />
                </aside>
              </div>
            </>
          )}
        </article>
      </div>
      <div className="wa-live" aria-hidden="true">
        <span className="wa-live-ic" />
        Ζωντανή Ροή Ειδήσεων
      </div>
      <div className="wa-cookies" aria-hidden="true">
        ΡΥΘΜΙΣΕΙΣ COOKIES
      </div>
    </div>
  );
}
